/**
 * A terminal observer has two different geometries:
 *
 * - the canonical geometry used by the Session Host VT parser; and
 * - the geometry of the observer's own screen.
 *
 * This module deliberately only describes the small xterm surface needed by
 * the projection. It does not import xterm, so Mobile and Desktop can share
 * the same projection rules without making the runtime package a renderer.
 */

export interface ProjectionCell {
  getChars(): string;
  getWidth(): number;
  isBold(): number;
  isItalic(): number;
  isDim(): number;
  isUnderline(): number;
  isBlink(): number;
  isInverse(): number;
  isInvisible(): number;
  isStrikethrough(): number;
  isOverline(): number;
  isFgRGB(): boolean;
  isFgPalette(): boolean;
  isFgDefault(): boolean;
  isBgRGB(): boolean;
  isBgPalette(): boolean;
  isBgDefault(): boolean;
  getFgColor(): number;
  getBgColor(): number;
}

export interface ProjectionLine {
  readonly isWrapped: boolean;
  readonly length: number;
  getCell(column: number, cell?: ProjectionCell): ProjectionCell | undefined;
}

export interface ProjectionBuffer {
  readonly type: "normal" | "alternate";
  readonly length: number;
  readonly baseY: number;
  readonly viewportY: number;
  /** Cursor coordinates are relative to the active screen base row. */
  readonly cursorX?: number;
  readonly cursorY?: number;
  getLine(row: number): ProjectionLine | undefined;
  getNullCell(): ProjectionCell;
}

export interface ProjectionTerminal {
  readonly rows: number;
  readonly cols: number;
  readonly buffer: {
    readonly active: ProjectionBuffer;
    readonly normal: ProjectionBuffer;
    readonly alternate: ProjectionBuffer;
  };
  reset(): void;
  resize(columns: number, rows: number): void;
  write(data: Uint8Array, callback?: () => void): void;
  scrollToBottom(): void;
  scrollToLine(line: number): void;
}

export interface RenderProjectionOptions {
  /** Do not include more canonical scrollback than this bounded amount. */
  maxLines?: number;
  /** Keep the observer at the same distance from the tail after repaint. */
  preserveScrollOffset?: number;
  /** Called after the visible xterm has consumed the projected VT bytes. */
  onComplete?: () => void;
  /** Update the current screen without clearing/rebuilding scrollback. */
  incremental?: boolean;
}

function sgrForCell(cell: ProjectionCell): string {
  const codes: string[] = [];
  if (cell.isBold()) codes.push("1");
  if (cell.isDim()) codes.push("2");
  if (cell.isItalic()) codes.push("3");
  if (cell.isUnderline()) codes.push("4");
  if (cell.isBlink()) codes.push("5");
  if (cell.isInverse()) codes.push("7");
  if (cell.isInvisible()) codes.push("8");
  if (cell.isStrikethrough()) codes.push("9");
  if (cell.isOverline()) codes.push("53");
  if (cell.isFgRGB()) {
    const color = cell.getFgColor();
    codes.push(`38;2;${(color >> 16) & 0xff};${(color >> 8) & 0xff};${color & 0xff}`);
  } else if (cell.isFgPalette()) {
    codes.push(`38;5;${cell.getFgColor()}`);
  } else if (!cell.isFgDefault()) {
    codes.push("39");
  }
  if (cell.isBgRGB()) {
    const color = cell.getBgColor();
    codes.push(`48;2;${(color >> 16) & 0xff};${(color >> 8) & 0xff};${color & 0xff}`);
  } else if (cell.isBgPalette()) {
    codes.push(`48;5;${cell.getBgColor()}`);
  } else if (!cell.isBgDefault()) {
    codes.push("49");
  }
  return codes.length ? `\u001b[${codes.join(";")}m` : "\u001b[0m";
}

function hasVisibleGlyph(chars: string): boolean {
  // Canonical rows are fixed-width. Whitespace at the end of a row is layout,
  // not content; sending it to a narrower observer makes xterm wrap that
  // padding into extra blank rows. Preserve whitespace inside the row by only
  // using this predicate to find the final content cell.
  return chars.length > 0 && /[^\s]/u.test(chars);
}

interface RenderedLine {
  readonly text: string;
  readonly width: number;
  readonly cells: readonly RenderedCell[];
  readonly cursorOffset?: number;
}

interface RenderedCell {
  readonly text: string;
  readonly width: number;
  readonly style: string;
}

function serializeCells(cells: readonly RenderedCell[]): string {
  let result = "";
  let activeStyle = "";
  for (const cell of cells) {
    if (cell.style !== activeStyle) {
      result += cell.style;
      activeStyle = cell.style;
    }
    result += cell.text;
  }
  return cells.length ? `${result}\u001b[0m` : "";
}

function hasVisualCell(cell: ProjectionCell): boolean {
  return hasVisibleGlyph(cell.getChars())
    || !cell.isFgDefault()
    || !cell.isBgDefault()
    || Boolean(
      cell.isBold()
      || cell.isDim()
      || cell.isItalic()
      || cell.isUnderline()
      || cell.isBlink()
      || cell.isInverse()
      || cell.isInvisible()
      || cell.isStrikethrough()
      || cell.isOverline(),
    );
}

function renderedLine(
  line: ProjectionLine,
  columns: number,
  nullCell: ProjectionCell,
  cursorColumn?: number,
): RenderedLine {
  let lastContentColumn = -1;
  const cells: ProjectionCell[] = [];
  const prefixWidths = [0];
  for (let column = 0; column < columns; column += 1) {
    // xterm's BufferLine API treats the second argument as a mutable work
    // cell and returns that same object. Do not retain it across columns:
    // doing so makes every saved cell alias the last column in the row.
    const cell = line.getCell(column) ?? nullCell;
    cells.push(cell);
    prefixWidths.push(prefixWidths[column] + Math.max(0, cell.getWidth()));
    if (cell.getWidth() !== 0 && hasVisualCell(cell)) lastContentColumn = column;
  }
  const cursorColumnClamped = cursorColumn === undefined
    ? undefined
    : Math.max(0, Math.min(columns, cursorColumn));
  const lastOutputColumn = Math.max(
    lastContentColumn,
    cursorColumnClamped === undefined ? -1 : cursorColumnClamped - 1,
  );
  if (lastOutputColumn < 0) {
    return {
      text: "",
      width: 0,
      cells: [],
      ...(cursorColumnClamped === 0 ? { cursorOffset: 0 } : {}),
    };
  }
  const renderedCells: RenderedCell[] = [];
  for (let column = 0; column <= lastOutputColumn; column += 1) {
    const cell = cells[column] ?? nullCell;
    if (cell.getWidth() === 0) continue;
    renderedCells.push({
      text: cell.getChars() || " ",
      width: Math.max(1, cell.getWidth()),
      style: sgrForCell(cell),
    });
  }
  return {
    text: serializeCells(renderedCells),
    width: prefixWidths[lastOutputColumn + 1] ?? 0,
    cells: renderedCells,
    ...(cursorColumnClamped === undefined
      ? {}
      : { cursorOffset: prefixWidths[cursorColumnClamped] ?? 0 }),
  };
}

interface ProjectionLayoutLine {
  text: string;
  width: number;
  cells: readonly RenderedCell[];
}

interface ProjectionCursor {
  lineIndex: number;
  offset: number;
}

interface ProjectionLayout {
  lines: ProjectionLayoutLine[];
  cursor?: ProjectionCursor;
}

function logicalLayout(
  buffer: ProjectionBuffer,
  columns: number,
  maxLines: number,
  trimTrailingEmpty = false,
  rangeStart = 0,
  rangeEnd = buffer.length,
  includeCursor = true,
): ProjectionLayout {
  const lines: ProjectionLayoutLine[] = [];
  const nullCell = buffer.getNullCell();
  const cursorPhysicalRow = includeCursor && buffer.cursorX !== undefined && buffer.cursorY !== undefined
    ? buffer.baseY + buffer.cursorY
    : -1;
  let cursor: ProjectionCursor | undefined;
  for (let index = rangeStart; index < Math.min(buffer.length, rangeEnd); index += 1) {
    const line = buffer.getLine(index);
    if (!line) continue;
    const isCursorRow = index === cursorPhysicalRow;
    const rendered = renderedLine(line, columns, nullCell, isCursorRow ? buffer.cursorX : undefined);
    if (line.isWrapped && lines.length > 0 && index !== rangeStart) {
      const previous = lines[lines.length - 1];
      const previousWidth = previous.width;
      previous.text += rendered.text;
      previous.width += rendered.width;
      previous.cells = [...previous.cells, ...rendered.cells];
      if (isCursorRow && rendered.cursorOffset !== undefined) {
        cursor = { lineIndex: lines.length - 1, offset: previousWidth + rendered.cursorOffset };
      }
    } else {
      lines.push({ text: rendered.text, width: rendered.width, cells: rendered.cells });
      if (isCursorRow && rendered.cursorOffset !== undefined) {
        cursor = { lineIndex: lines.length - 1, offset: rendered.cursorOffset };
      }
    }
  }
  // The normal buffer includes the PTY's current viewport. Its unused rows
  // after the last rendered glyph are layout, not history. Keeping them in a
  // textual observer bootstrap makes scrollToBottom() land above the actual
  // prompt and produces a large black tail on a taller mobile viewport.
  // Alternate-screen applications own their full row layout, so callers do
  // not enable this for that buffer.
  if (trimTrailingEmpty) {
    while (
      lines.length > 1
      && lines[lines.length - 1].text === ""
      && cursor?.lineIndex !== lines.length - 1
    ) {
      const removedIndex = lines.length - 1;
      lines.pop();
      if (cursor?.lineIndex === removedIndex) cursor = undefined;
    }
  }
  if (lines.length > maxLines) {
    const firstKept = lines.length - maxLines;
    lines.splice(0, firstKept);
    if (cursor) {
      cursor.lineIndex -= firstKept;
      if (cursor.lineIndex < 0) cursor = undefined;
    }
  }
  return { lines, cursor };
}

interface WrappedProjectionLayout {
  lines: ProjectionLayoutLine[];
  cursor?: ProjectionCursor;
}

function wrapProjectionLayout(
  layout: ProjectionLayout,
  columns: number,
): WrappedProjectionLayout {
  const targetColumns = Math.max(1, columns);
  const lines: ProjectionLayoutLine[] = [];
  let cursor: ProjectionCursor | undefined;
  for (let index = 0; index < layout.lines.length; index += 1) {
    const source = layout.lines[index];
    const chunks: ProjectionLayoutLine[] = [];
    let chunkCells: RenderedCell[] = [];
    let chunkWidth = 0;
    const flush = () => {
      chunks.push({
        text: serializeCells(chunkCells),
        width: chunkWidth,
        cells: chunkCells,
      });
      chunkCells = [];
      chunkWidth = 0;
    };
    if (!source.cells.length) {
      chunks.push({ text: "", width: 0, cells: [] });
    } else {
      for (const cell of source.cells) {
        if (chunkCells.length && chunkWidth + cell.width > targetColumns) flush();
        chunkCells.push(cell);
        chunkWidth += cell.width;
        if (chunkWidth >= targetColumns) flush();
      }
      if (chunkCells.length) flush();
    }
    const firstChunk = lines.length;
    lines.push(...chunks);
    if (layout.cursor?.lineIndex === index) {
      const offset = layout.cursor.offset;
      let remaining = offset;
      let chunkIndex = 0;
      while (chunkIndex < chunks.length - 1 && remaining >= chunks[chunkIndex].width) {
        remaining -= chunks[chunkIndex].width;
        chunkIndex += 1;
      }
      // A cursor at an exact full-line boundary is the xterm pending-wrap
      // position. Keep it in the preceding visible cell instead of letting
      // the next explicit CRLF create an extra blank row.
      if (remaining === 0 && chunkIndex > 0) {
        chunkIndex -= 1;
        remaining = chunks[chunkIndex].width;
      }
      cursor = { lineIndex: firstChunk + chunkIndex, offset: remaining };
    }
  }
  return { lines, cursor };
}

function cursorBytes(
  cursor: ProjectionCursor | undefined,
  lines: ProjectionLayoutLine[],
  rows: number,
  columns: number,
  precedingRows = 0,
): string {
  if (!cursor) return "";
  const targetColumns = Math.max(1, columns);
  let renderedRow = precedingRows;
  for (let index = 0; index < cursor.lineIndex; index += 1) {
    renderedRow += 1;
  }
  // A cursor exactly at a full line's right edge is rendered by xterm in the
  // final cell of that row until another character arrives. CUP cannot target
  // the pending-wrap column, so use that visible cell.
  const offset = cursor.offset > 0 && cursor.offset % targetColumns === 0
    ? cursor.offset - 1
    : cursor.offset;
  renderedRow += Math.floor(offset / targetColumns);
  const renderedColumn = offset % targetColumns;
  const totalRows = precedingRows + lines.length;
  const viewportTop = Math.max(0, totalRows - Math.max(1, rows));
  const row = Math.max(0, Math.min(Math.max(1, rows) - 1, renderedRow - viewportTop));
  const column = Math.max(0, Math.min(targetColumns - 1, renderedColumn));
  return `\u001b[${row + 1};${column + 1}H`;
}

function projectionBytes(canonical: ProjectionTerminal, displayColumns: number, displayRows: number, maxLines: number): Uint8Array {
  const normal = logicalLayout(
    canonical.buffer.normal,
    canonical.cols,
    maxLines,
    true,
    0,
    canonical.buffer.normal.length,
    canonical.buffer.active.type !== "alternate",
  );
  const active = canonical.buffer.active.type === "alternate"
    ? logicalLayout(canonical.buffer.alternate, canonical.cols, maxLines, false)
    : { lines: [], cursor: undefined };
  const logicalLines = canonical.buffer.active.type === "alternate"
    ? [...normal.lines, ...active.lines]
    : normal.lines;
  const logicalCursor = canonical.buffer.active.type === "alternate"
    ? active.cursor
      ? { lineIndex: normal.lines.length + active.cursor.lineIndex, offset: active.cursor.offset }
      : undefined
    : normal.cursor;
  const wrapped = wrapProjectionLayout({ lines: logicalLines, cursor: logicalCursor }, displayColumns);
  const text = wrapped.lines.length ? wrapped.lines.map((line) => line.text).join("\r\n") : "";
  const cursor = cursorBytes(wrapped.cursor, wrapped.lines, displayRows, displayColumns);
  // Disable xterm's implicit wrap while writing explicitly segmented rows.
  // This prevents a full-width segment followed by CRLF from creating a
  // second line on alternate-screen TUIs such as OpenCode.
  return new TextEncoder().encode(`\u001b[?7l\u001b[2J\u001b[H${text}\u001b[?7h${cursor}`);
}

/**
 * Paint only the active screen in place. This is used for live frames while
 * the canonical scrollback shape is unchanged. Unlike a full projection it
 * does not reset xterm, so one-character PTY echoes cannot flash the screen.
 */
function incrementalProjectionBytes(canonical: ProjectionTerminal, displayColumns: number, displayRows: number): Uint8Array {
  const active = canonical.buffer.active;
  const start = active.type === "normal" ? Math.max(0, active.viewportY) : 0;
  const end = Math.min(active.length, start + canonical.rows);
  const logical = logicalLayout(active, canonical.cols, canonical.rows, false, start, end);
  const wrapped = wrapProjectionLayout(logical, displayColumns);
  const lines = wrapped.lines.map((line) => `${line.text}\u001b[K`);
  while (lines.length < displayRows) lines.push("\u001b[K");
  const body = lines.join("\r\n");
  const cursor = cursorBytes(wrapped.cursor, wrapped.lines, displayRows, displayColumns);
  return new TextEncoder().encode(`\u001b[?7l\u001b[H${body}\u001b[J\u001b[?7h${cursor}`);
}

/**
 * Repaint only the observer-local xterm. The canonical parser is never
 * resized, reset, or otherwise mutated by a viewport/layout change.
 */
export function renderObserverProjection(
  canonical: ProjectionTerminal,
  display: ProjectionTerminal,
  options: RenderProjectionOptions = {},
): void {
  const offset = options.preserveScrollOffset ?? Math.max(
    0,
    display.buffer.active.baseY - display.buffer.active.viewportY,
  );
  const bytes = options.incremental
    ? incrementalProjectionBytes(canonical, display.cols, display.rows)
    : projectionBytes(canonical, display.cols, display.rows, options.maxLines ?? 10_000);
  if (!options.incremental) display.reset();
  display.write(bytes, () => {
    if (offset > 0) display.scrollToLine(Math.max(0, display.buffer.active.baseY - offset));
    else display.scrollToBottom();
    options.onComplete?.();
  });
}
